import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import { ArrowLeft, CreditCard, Lock, Loader2 } from 'lucide-react';

import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { adminApi } from './adminApi';

/**
 * SuperAdmin → License Pricing.
 *
 * Lets a technical_expert (or superadmin) change the one-time Pro upgrade
 * price (₹ INR). The save flow is OTP-gated:
 *   1. Admin enters the new amount + clicks "Save (OTP required)".
 *   2. Backend dispatches an Email OTP via the existing admin_otp service
 *      with purpose='edit_pricing'.
 *   3. Admin enters the 6-digit code and submits the PUT.
 *
 * The page surfaces the LAST-MODIFIED metadata so multiple admins can
 * see who changed the price most recently.
 *
 * Themed to match the SuperAdmin shell — same radial-gradient background,
 * slate-900 glass cards, amber accents — as the Email/WhatsApp/Profile
 * pages.
 */
export function AdminLicensePricingPage() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sendingOtp, setSendingOtp] = useState(false);
  const [config, setConfig] = useState({
    pro_upgrade_inr: 0,
    default_inr: 1999,
    min_inr: 1,
    max_inr: 1_000_000,
    updated_at: null,
    updated_by_email: null,
  });
  const [editedInr, setEditedInr] = useState('');
  // OTP state — flipped to `true` once the request-otp endpoint succeeds.
  const [otpRequested, setOtpRequested] = useState(false);
  const [otp, setOtp] = useState('');
  const [emailMasked, setEmailMasked] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const r = await adminApi.get('/admin/license-pricing');
      setConfig(r.data);
      setEditedInr(String(r.data.pro_upgrade_inr ?? r.data.default_inr ?? 1999));
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load pricing');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const dirty =
    String(editedInr).trim() !== '' &&
    Number(editedInr) !== Number(config.pro_upgrade_inr);

  const validateInr = () => {
    const n = Number(editedInr);
    if (!Number.isFinite(n) || !Number.isInteger(n)) {
      toast.error('Amount must be a whole number (₹).');
      return false;
    }
    if (n < config.min_inr || n > config.max_inr) {
      toast.error(`Amount must be between ₹${config.min_inr} and ₹${config.max_inr.toLocaleString('en-IN')}.`);
      return false;
    }
    return true;
  };

  const requestOtp = async () => {
    if (!dirty) {
      toast.error('No changes to save.');
      return;
    }
    if (!validateInr()) return;
    setSendingOtp(true);
    try {
      const r = await adminApi.post('/admin/license-pricing/request-otp');
      if (r.data?.ok === false) {
        toast.error(r.data?.error || 'Could not send OTP.');
        return;
      }
      setEmailMasked(r.data?.email_masked || '');
      setOtpRequested(true);
      setOtp('');
      toast.success(r.data?.message || 'OTP sent to your email.');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to send OTP');
    } finally {
      setSendingOtp(false);
    }
  };

  const save = async () => {
    if (!otp || otp.length !== 6) {
      toast.error('Enter the 6-digit OTP from your email.');
      return;
    }
    if (!validateInr()) return;
    setSaving(true);
    try {
      const r = await adminApi.put('/admin/license-pricing', {
        pro_upgrade_inr: Number(editedInr),
        otp,
      });
      toast.success(`Price updated to ₹${r.data.pro_upgrade_inr.toLocaleString('en-IN')}`);
      setOtpRequested(false);
      setOtp('');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save price');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      data-testid="admin-license-pricing-page"
      className="min-h-screen"
      style={{
        background: 'radial-gradient(circle at 0% 0%, #1e293b 0%, #0f172a 50%, #020617 100%)',
      }}
    >
      <header className="border-b border-slate-800 bg-slate-950/60 backdrop-blur-xl">
        <div className="max-w-2xl mx-auto px-6 py-4 flex items-center gap-3">
          <Link
            to="/admin"
            data-testid="back-to-dashboard"
            className="w-9 h-9 rounded-lg bg-slate-900 border border-slate-800 flex items-center justify-center hover:bg-slate-800"
          >
            <ArrowLeft className="w-4 h-4 text-slate-300" />
          </Link>
          <div className="w-9 h-9 rounded-lg bg-amber-500/10 border border-amber-500/30 flex items-center justify-center">
            <CreditCard className="w-4 h-4 text-amber-400" />
          </div>
          <div>
            <div className="text-white font-semibold">License Pricing</div>
            <div className="text-xs text-slate-400">
              One-time Pro upgrade price shown to every customer in the Upgrade-to-Pro modal.
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-6 py-8 space-y-5">
        {loading ? (
          <div className="flex items-center gap-2 text-slate-400 text-sm py-10 justify-center">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading…
          </div>
        ) : (
          <>
            {/* Current price + last-modified metadata */}
            <div className="bg-slate-900/70 border border-slate-800 rounded-xl p-5 flex flex-wrap items-center justify-between gap-3">
              <div>
                <div className="text-xs uppercase tracking-wider text-slate-400 font-semibold">
                  Current Price
                </div>
                <div
                  className="text-3xl font-bold text-white mt-0.5"
                  data-testid="current-pro-price"
                >
                  ₹{Number(config.pro_upgrade_inr).toLocaleString('en-IN')}
                </div>
              </div>
              {config.updated_at ? (
                <div className="text-right text-xs text-slate-400">
                  <div>
                    Last edited{' '}
                    <span className="text-slate-200">
                      {new Date(config.updated_at).toLocaleString('en-IN')}
                    </span>
                  </div>
                  {config.updated_by_email && (
                    <div className="text-slate-500">by {config.updated_by_email}</div>
                  )}
                </div>
              ) : (
                <span className="text-xs text-slate-500">Never edited — using default</span>
              )}
            </div>

            {/* Edit field */}
            <div className="bg-slate-900/70 border border-slate-800 rounded-xl p-5 space-y-3">
              <Label className="text-xs text-slate-300 uppercase tracking-wider">
                New price (amount in ₹)
              </Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 pointer-events-none">
                  ₹
                </span>
                <Input
                  type="number"
                  min={config.min_inr}
                  max={config.max_inr}
                  step={1}
                  value={editedInr}
                  onChange={(e) => {
                    setEditedInr(e.target.value);
                    // Invalidate any pending OTP so the admin must re-send
                    // after each amount change — protects against
                    // social-engineered last-second swaps.
                    if (otpRequested) {
                      setOtpRequested(false);
                      setOtp('');
                    }
                  }}
                  className="pl-7 bg-slate-800/50 border-slate-700 text-white"
                  data-testid="pricing-amount-input"
                  placeholder={String(config.default_inr)}
                />
              </div>
              <p className="text-xs text-slate-500">
                Whole rupees only. Range ₹{config.min_inr.toLocaleString('en-IN')} – ₹
                {config.max_inr.toLocaleString('en-IN')}. Saved value applies instantly
                to every Pro upgrade order created after the save.
              </p>

              {/* OTP request + verify */}
              {!otpRequested ? (
                <div className="flex gap-2 pt-1">
                  <Button
                    onClick={requestOtp}
                    disabled={sendingOtp || !dirty}
                    data-testid="pricing-request-otp-btn"
                    className="bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold"
                  >
                    {sendingOtp ? (
                      <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
                    ) : (
                      <Lock className="w-4 h-4 mr-1.5" />
                    )}
                    {sendingOtp ? 'Sending OTP…' : 'Save (OTP required)'}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={load}
                    disabled={sendingOtp}
                    className="border-slate-700 bg-slate-900 text-slate-200 hover:bg-slate-800"
                  >
                    Reset
                  </Button>
                </div>
              ) : (
                <div className="mt-2 rounded-xl border border-amber-500/40 bg-amber-500/10 p-4 space-y-3">
                  <div className="flex items-start gap-2 text-amber-200">
                    <Lock className="w-4 h-4 mt-0.5" />
                    <div className="text-sm">
                      OTP sent to{' '}
                      <strong className="text-white">
                        {emailMasked || 'your registered email'}
                      </strong>
                      . Enter the 6-digit code to confirm the new price.
                    </div>
                  </div>
                  <Input
                    type="text"
                    maxLength={6}
                    value={otp}
                    onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="000000"
                    className="text-center text-lg tracking-widest bg-slate-800/50 border-slate-700 text-white font-mono"
                    data-testid="pricing-otp-input"
                    autoComplete="one-time-code"
                  />
                  <div className="flex gap-2">
                    <Button
                      onClick={save}
                      disabled={saving || otp.length !== 6}
                      data-testid="pricing-save-confirm-btn"
                      className="bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold flex-1"
                    >
                      {saving && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />}
                      {saving
                        ? 'Saving…'
                        : `Confirm — change price to ₹${Number(editedInr || 0).toLocaleString('en-IN')}`}
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => {
                        setOtpRequested(false);
                        setOtp('');
                      }}
                      disabled={saving}
                      className="border-slate-700 bg-slate-900 text-slate-200 hover:bg-slate-800"
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </main>
    </div>
  );
}

export default AdminLicensePricingPage;
